<template>
  <div>
    <NuxtLayout name="default">
      <!-- Hero section -->
      <HeroStandardApollo
        :bg-image-source="$page?.studentsTutoring?.heroImage!"
        :title="$page?.studentsTutoring?.title!"
        title-size="large"
        :display-gradient="true"
        :gradient-opacity="0.85"
        :full-width="false"
      >
      </HeroStandardApollo>

      <!-- intro section -->
      <SectionApollo
        :title="$page?.studentsTutoring?.introTitle!"
        :image="$page?.studentsTutoring?.introImage!"
      >
        <div v-html="$page?.studentsTutoring?.introDescription"></div>
      </SectionApollo>

      <!-- online-tutoring-resources section -->
      <SectionContainerAtlas
        class="faq-section"
        :title="$page?.studentsTutoring?.subjectTitle!"
        border-variant="light-4"
        container-padding="p-space-sm p-lg-space-xl"
        section-image-source="https://cms.asuonline.asu.edu/sites/default/files/2021-08/NetworkV02-01.png"
        section-background-variant="white"
      >
        <template #mainContent>
          <div class="mb-space-sm" v-html="$page?.studentsTutoring?.subjectDescription!"></div>
          <div class="mb-space-lg">
            <NuxtLink
              :href="$page?.studentsTutoring?.subjectLink?.uri!"
              role="button"
              class="btn btn-secondary fw-bold fs-medium px-space-xs"
              @click="
                handleCtaClick(
                  $page?.studentsTutoring?.subjectTitle!,
                  $page?.studentsTutoring?.subjectLink?.text!
                )
              "
            >
              {{ $page?.studentsTutoring?.subjectLink?.text }}
            </NuxtLink>
          </div>
          <div class="bg-secondary title-underline mb-space-md"></div>
          <div class="row">
            <div
              v-for="(card, index) in $page?.studentsTutoring?.subjectCards"
              :key="index"
              class="mb-space-sm mb-lg-space-md col-12 col-lg-6"
            >
              <CardImageTile
                :title="card?.title!"
                :bg-image-src="card?.image!"
                :text="card?.text!"
                :cta-text="card?.link?.text!"
                :cta-link="card?.link?.uri!"
                :display-cta-button="true"
                :cta-link-target="handleCtaTarget(card?.link?.target!)"
                @card-click="
                  ($event) =>
                    handleCardClick(
                      $event,
                      $page?.studentsTutoring?.subjectTitle!,
                      card?.title!,
                      'hover card',
                      card?.link?.uri!
                    )
                "
                @cta-click="
                  ($event) =>
                    handleCardClick(
                      $event,
                      $page?.studentsTutoring?.subjectTitle!,
                      card?.title!,
                      'hover card',
                      card?.link?.uri!
                    )
                "
                @card-enter.prevent="handleEnterKeyEvent(card?.link?.uri!)"
              ></CardImageTile>
            </div>
          </div>
        </template>
      </SectionContainerAtlas>

      <!-- section tutoring anytime anywhere anywhere - section-atlas -->
      <SectionApollo
        :title="$page?.studentsTutoring?.anytimeTitle!"
        :image="$page?.studentsTutoring?.anytimeImage!"
        image-position="right"
        section-background-variant="dark-3"
        text-background-variant="dark-3"
        title-variant="white"
      >
        <div
          class="text-white mb-space-sm"
          v-html="$page?.studentsTutoring?.anytimeDescription"
        ></div>
        <NuxtLink
          :href="$page?.studentsTutoring?.anytimeLink?.uri!"
          role="button"
          class="btn btn-secondary fw-bold fs-medium px-space-xs accessStudy"
          @click="
            handleCtaClick(
              $page?.studentsTutoring?.anytimeTitle!,
              $page?.studentsTutoring?.anytimeLink?.text!
            )
          "
        >
          {{ $page?.studentsTutoring?.anytimeLink?.text }}
        </NuxtLink>
      </SectionApollo>
      <!-- explore-your-future section -->
      <SectionExploreYourFuture
        :title="$page?.studentsTutoring?.exploreTitle!"
        :items="$page?.studentsTutoring?.exploreCards! as ExploreItem[]"
      />

      <!-- section-rfi-multistep -->
      <SectionRfiMultiStep :bg-image="RfiBgImage" title="Request information"></SectionRfiMultiStep>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardImageTile } from "@rds-vue-ui/card-image-tile";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import type { ExploreItem } from "~/components/students/SectionExploreYourFuture.vue";
import SectionExploreYourFuture from "~/components/students/SectionExploreYourFuture.vue";
import { useUtilityService } from "~/composables/useUtilityService";
import type { StudentsTutoring } from "~/server/resolvers/resolvers-types";
import type { StudentsTutoringQuery } from "~/types/graphql-operations";

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

const config = useRuntimeConfig();

const UtilityService = useUtilityService();

const $page = await usePageQuery<StudentsTutoringQuery>(
  /* GraphQL */ `
    #graphql
    query StudentsTutoring($studentsTutoringId: ID!) {
      studentsTutoring(id: $studentsTutoringId) {
        detailPage
        metaTitle
        metaDescription
        heroImage
        title
        introTitle
        introImage
        introDescription
        subjectTitle
        subjectDescription
        subjectLink {
          uri
          text
          target
        }
        subjectCards {
          id
          title
          image
          text
          link {
            uri
            text
            target
          }
        }
        anytimeTitle
        anytimeImage
        anytimeDescription
        anytimeLink {
          uri
          text
          target
        }
        exploreTitle
        exploreBg
        exploreCards {
          title
          image
          link {
            uri
            target
          }
        }
      }
    }
  `,
  {
    studentsTutoringId: "466f78cb-052a-4464-8642-ddf775ef1642"
  },
  {
    key: "students-tutoring"
  }
);

useHead({
  title: $page?.studentsTutoring?.metaTitle,
  meta: [
    { name: "Title", content: $page?.studentsTutoring?.metaTitle },
    {
      name: "Description",
      content: $page?.studentsTutoring?.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.studentsTutoring?.detailPage}`
      )
    }
  ],
  script: [
    useSchemaService().generateStudentsTutoringSchema(
      $page?.studentsTutoring as unknown as StudentsTutoring
    )
  ]
});

const handleCtaTarget = (target: string) => {
  return target === "BLNK" ? "_blank" : "_self";
};

const handleCtaClick = (title: string, ctaText: string) => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    title.toLowerCase(),
    ctaText.toLowerCase()
  );
};

const handleCardClick = (
  event: Event,
  section: string,
  text: string,
  component: string,
  uri: string
) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    component.toLowerCase()
  );

  navigateTo(uri, {
    open: {
      target: "_blank"
    }
  });
};

const handleEnterKeyEvent = (uri: string) => {
  navigateTo(uri, {
    open: {
      target: "_blank"
    }
  });
};
</script>

<style lang="scss" scoped>
.accessStudy:focus-visible {
  outline: 2px solid white;
}

.faq-section :deep(.block-container) {
  max-width: 920px;
}
</style>
